import { useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { Dialog, RowMenu, toast } from '../ui.js';

/**
 * Withdraw a review, and remove a withdrawn version for good (owner 2026-11-02, thread "Ta bort videor, granskningar och
 * projekt"). Under Fler val on the review page; each asks first. Nothing is created in Ziflow and nobody is emailed.
 */

const TX = texts(
  {
    more: 'More options',
    withdrawMenu: 'Withdraw the review…',
    removeMenu: 'Remove the version for good…',
    withdrawTitle: (n: number) => `Withdraw version ${n}?`,
    withdrawBody: 'The review closes, reminders stop and the client no longer sees this version. The video goes back to editing. Nobody is emailed: tell the client yourself if they had it.',
    reason: 'Why? (optional)',
    withdraw: 'Withdraw',
    withdrawn: 'The review is withdrawn',
    removeTitle: (n: number) => `Remove version ${n} for good?`,
    removeBody: 'The version and its comments are removed. It cannot be undone. The files stay in Drive.',
    understand: 'I understand that this cannot be undone',
    remove: 'Remove for good',
    removed: 'The version is removed',
    keep: 'Keep it'
  },
  {
    more: 'Fler val',
    withdrawMenu: 'Dra tillbaka granskningen…',
    removeMenu: 'Ta bort versionen för gott…',
    withdrawTitle: (n: number) => `Dra tillbaka version ${n}?`,
    withdrawBody: 'Granskningen stängs, påminnelserna slutar och kunden ser inte längre den här versionen. Videon går tillbaka till redigering. Inget mejl skickas: säg till kunden själv om de hade fått den.',
    reason: 'Varför? (valfritt)',
    withdraw: 'Dra tillbaka',
    withdrawn: 'Granskningen är tillbakadragen',
    removeTitle: (n: number) => `Ta bort version ${n} för gott?`,
    removeBody: 'Versionen och dess kommentarer tas bort. Det går inte att ångra. Filerna finns kvar i Drive.',
    understand: 'Jag förstår att det inte går att ångra',
    remove: 'Ta bort för gott',
    removed: 'Versionen är borttagen',
    keep: 'Behåll'
  }
);

export const withdrawTexts = TX;

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The review page's Fler val: withdraw (open versions) or remove for good (withdrawn ones). */
export function VersionMenu(p: { api: Api; base: string; version: { id: string; number: number; can: { withdraw?: boolean; remove?: boolean } }; onDone: (removed: boolean) => void }) {
  const t = TX[useLang()];
  const [open, setOpen] = useState<'withdraw' | 'remove' | null>(null);
  const items = [
    ...(p.version.can.withdraw ? [{ label: t.withdrawMenu, danger: true, run: () => setOpen('withdraw') }] : []),
    ...(p.version.can.remove ? [{ label: t.removeMenu, danger: true, run: () => setOpen('remove') }] : [])
  ];
  if (!items.length) return null;
  return (
    <>
      <RowMenu label={t.more} items={items} />
      {open === 'withdraw' ? <WithdrawDialog {...p} onClose={() => setOpen(null)} /> : null}
      {open === 'remove' ? <RemoveVersionDialog {...p} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function WithdrawDialog(p: { api: Api; base: string; version: { id: string; number: number }; onClose: () => void; onDone: (removed: boolean) => void }) {
  const t = TX[useLang()];
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await p.api.post(`${p.base}/review/${p.version.id}/withdraw`, { reason: reason.trim() });
      toast({ text: t.withdrawn });
      p.onClose();
      p.onDone(false);
    } catch (e) {
      toast({ text: errText(e) });
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.withdrawTitle(p.version.number)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.keep}
          </button>
          <button type="button" class="btn btn-danger" disabled={busy} onClick={() => void run()}>
            {t.withdraw}
          </button>
        </>
      }
    >
      <div class="stack">
        <p>{t.withdrawBody}</p>
        <div class="field">
          <label class="label" for="wr-reason">
            {t.reason}
          </label>
          <textarea id="wr-reason" class="textarea" rows={2} maxLength={300} value={reason} onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)} />
        </div>
      </div>
    </Dialog>
  );
}

function RemoveVersionDialog(p: { api: Api; base: string; version: { id: string; number: number }; onClose: () => void; onDone: (removed: boolean) => void }) {
  const t = TX[useLang()];
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await p.api.post(`${p.base}/review/${p.version.id}/remove`, { confirm: true });
      toast({ text: t.removed });
      p.onClose();
      p.onDone(true);
    } catch (e) {
      toast({ text: errText(e) });
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.removeTitle(p.version.number)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.keep}
          </button>
          <button type="button" class="btn btn-danger-solid" disabled={!sure || busy} onClick={() => void run()}>
            {t.remove}
          </button>
        </>
      }
    >
      <div class="stack">
        <p>{t.removeBody}</p>
        <label class="check">
          <input type="checkbox" checked={sure} onChange={(e) => setSure((e.currentTarget as HTMLInputElement).checked)} /> {t.understand}
        </label>
      </div>
    </Dialog>
  );
}
